import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { TrendingUp, TrendingDown, Search, ArrowUpDown, Filter, RefreshCw, ChevronLeft, ChevronRight, Percent, Target, Menu, X, SlidersHorizontal } from 'lucide-react';
import { fetchApi, formatPrice, formatPercent, formatNumber, getChangeColor, cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

export default function StocksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ sectors: [], countries: [], exchanges: [] });
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [sector, setSector] = useState('');
  const [country, setCountry] = useState('');
  const [sortBy, setSortBy] = useState('ticker');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [predictionFilter, setPredictionFilter] = useState('');
  const [confidenceFilter, setConfidenceFilter] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const limit = 20;

  const loadStocks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sector && sector !== "all") params.set('sector', sector);
      if (country && country !== "all") params.set('country', country);
      if (predictionFilter) params.set('prediction_filter', predictionFilter);
      if (confidenceFilter) params.set('confidence_filter', confidenceFilter);
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
      params.set('limit', limit.toString());
      params.set('offset', ((page - 1) * limit).toString());

      const data = await fetchApi(`/stocks?${params.toString()}`);
      setStocks(data.stocks || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load stocks:', error);
    } finally {
      setLoading(false);
    }
  }, [sector, country, sortBy, sortOrder, page, predictionFilter, confidenceFilter]);

  const loadFilters = useCallback(async () => {
    try {
      const data = await fetchApi('/filters');
      setFilters(data);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadStocks();
      return;
    }
    
    setLoading(true);
    try {
      const data = await fetchApi(`/stocks/search?q=${encodeURIComponent(searchQuery)}`);
      setStocks(data.stocks || []);
      setTotal(data.stocks?.length || 0);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPrices = async () => {
    setRefreshing(true);
    try {
      await fetchApi('/refresh-prices', { method: 'POST' });
      await loadStocks();
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadFilters();
    // Initialize stocks if needed
    fetchApi('/init-stocks', { method: 'POST' }).catch(() => {});
  }, [loadFilters]);

  useEffect(() => {
    if (searchQuery) {
      handleSearch();
    } else {
      loadStocks();
    }
  }, [sector, country, sortBy, sortOrder, page, loadStocks]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg sm:text-xl">MarketMind</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/forum" className="text-muted-foreground hover:text-foreground transition-colors">
              Forum
            </Link>
            <Link to="/search" className="text-muted-foreground hover:text-foreground transition-colors">
              Advanced Search
            </Link>
            {isAuthenticated ? (
              <Button data-testid="stocks-dashboard-btn" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            ) : (
              <Button data-testid="stocks-signin-btn" onClick={() => navigate('/auth')}>
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-card">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-6">
                <Link 
                  to="/forum" 
                  className="text-foreground hover:text-primary transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Forum
                </Link>
                <Link 
                  to="/search" 
                  className="text-foreground hover:text-primary transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Advanced Search
                </Link>
                {isAuthenticated ? (
                  <Button 
                    data-testid="mobile-dashboard-btn" 
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full"
                  >
                    Dashboard
                  </Button>
                ) : (
                  <Button 
                    data-testid="mobile-signin-btn" 
                    onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                    className="w-full"
                  >
                    Sign In
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight mb-2">
            Stock Browser
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Explore {total} tracked stocks with AI-powered predictions
          </p>
        </div>

        {/* Search Bar - Always Visible */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="stocks-search-input"
              placeholder="Search ticker or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-9"
            />
          </div>
          <Button data-testid="stocks-search-btn" onClick={handleSearch} variant="outline" className="shrink-0">
            <Search className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Search</span>
          </Button>
        </div>

        {/* Mobile Filters Button + Refresh */}
        <div className="flex gap-2 mb-4 md:hidden">
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="flex-1" data-testid="mobile-filters-btn">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {(sector || country || predictionFilter || confidenceFilter) && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {[sector, country, predictionFilter, confidenceFilter].filter(Boolean).length}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[85vh] bg-card rounded-t-xl">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-6 overflow-y-auto pb-8">
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Sector</label>
                  <Select value={sector || "all"} onValueChange={(v) => setSector(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="mobile-sector-filter" className="w-full">
                      <SelectValue placeholder="All Sectors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sectors</SelectItem>
                      {filters.sectors.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Country</label>
                  <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="mobile-country-filter" className="w-full">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {filters.countries.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Prediction</label>
                  <Select value={predictionFilter || "all"} onValueChange={(v) => setPredictionFilter(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="mobile-prediction-filter" className="w-full">
                      <SelectValue placeholder="All Predictions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Predictions</SelectItem>
                      <SelectItem value="up_10">Bullish &gt; +10%</SelectItem>
                      <SelectItem value="up_5">Bullish &gt; +5%</SelectItem>
                      <SelectItem value="up_any">Any Bullish</SelectItem>
                      <SelectItem value="down_any">Any Bearish</SelectItem>
                      <SelectItem value="down_5">Bearish &lt; -5%</SelectItem>
                      <SelectItem value="down_10">Bearish &lt; -10%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Confidence</label>
                  <Select value={confidenceFilter || "all"} onValueChange={(v) => setConfidenceFilter(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="mobile-confidence-filter" className="w-full">
                      <SelectValue placeholder="Any Confidence" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Confidence</SelectItem>
                      <SelectItem value="high">High (&gt;70%)</SelectItem>
                      <SelectItem value="medium">Medium (50-70%)</SelectItem>
                      <SelectItem value="low">Low (&lt;50%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={() => setFiltersOpen(false)} 
                  className="w-full mt-4"
                >
                  Apply Filters
                </Button>
                
                {(sector || country || predictionFilter || confidenceFilter) && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setSector('');
                      setCountry('');
                      setPredictionFilter('');
                      setConfidenceFilter('');
                    }} 
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>

          <Button
            data-testid="mobile-refresh-btn"
            variant="outline"
            onClick={handleRefreshPrices}
            disabled={refreshing}
          >
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
          </Button>
        </div>

        {/* Desktop Filters Row */}
        <div className="hidden md:flex flex-wrap gap-3 mb-6">
          <Select value={sector || "all"} onValueChange={(v) => setSector(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="sector-filter" className="w-40">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sector" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {filters.sectors.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="country-filter" className="w-40">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {filters.countries.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={predictionFilter || "all"} onValueChange={(v) => setPredictionFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="prediction-filter" className="w-44">
              <Target className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Prediction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Predictions</SelectItem>
              <SelectItem value="up_10">Bullish &gt; +10%</SelectItem>
              <SelectItem value="up_5">Bullish &gt; +5%</SelectItem>
              <SelectItem value="up_any">Any Bullish</SelectItem>
              <SelectItem value="down_any">Any Bearish</SelectItem>
              <SelectItem value="down_5">Bearish &lt; -5%</SelectItem>
              <SelectItem value="down_10">Bearish &lt; -10%</SelectItem>
            </SelectContent>
          </Select>

          <Select value={confidenceFilter || "all"} onValueChange={(v) => setConfidenceFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="confidence-filter" className="w-40">
              <Percent className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Confidence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Confidence</SelectItem>
              <SelectItem value="high">High (&gt;70%)</SelectItem>
              <SelectItem value="medium">Medium (50-70%)</SelectItem>
              <SelectItem value="low">Low (&lt;50%)</SelectItem>
            </SelectContent>
          </Select>

          <Button
            data-testid="refresh-prices-btn"
            variant="outline"
            onClick={handleRefreshPrices}
            disabled={refreshing}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Stocks List */}
        <Card className="bg-card border-border/50">
          <CardContent className="p-0">
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left p-4">
                      <button
                        onClick={() => toggleSort('ticker')}
                        className="flex items-center gap-1 text-muted-foreground hover:text-foreground font-medium"
                      >
                        Symbol <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-left p-4">Company</th>
                    <th className="text-left p-4">Sector</th>
                    <th className="text-right p-4">
                      <button
                        onClick={() => toggleSort('current_price')}
                        className="flex items-center gap-1 text-muted-foreground hover:text-foreground font-medium ml-auto"
                      >
                        Price <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-right p-4">24h Change</th>
                    <th className="text-right p-4">Market Cap</th>
                    <th className="text-right p-4">
                      <button
                        onClick={() => toggleSort('predicted_change')}
                        className="flex items-center gap-1 text-muted-foreground hover:text-foreground font-medium ml-auto"
                      >
                        AI Prediction <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-right p-4">
                      <button
                        onClick={() => toggleSort('confidence')}
                        className="flex items-center gap-1 text-muted-foreground hover:text-foreground font-medium ml-auto"
                      >
                        Confidence <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="text-right p-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i} className="border-b border-border/50">
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j} className="p-4">
                            <Skeleton className="h-5 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : stocks.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-8 text-center text-muted-foreground">
                        No stocks found. Try adjusting your search or filters.
                      </td>
                    </tr>
                  ) : (
                    stocks.map((stock) => (
                      <tr
                        key={stock.stock_id}
                        className="border-b border-border/50 hover:bg-accent/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/stocks/${stock.ticker}`)}
                        data-testid={`stock-row-${stock.ticker}`}
                      >
                        <td className="p-4">
                          <span className="font-mono font-semibold">{stock.ticker}</span>
                        </td>
                        <td className="p-4">
                          <span className="text-muted-foreground">{stock.name}</span>
                        </td>
                        <td className="p-4">
                          {stock.sector && (
                            <Badge variant="outline" className="font-normal">
                              {stock.sector}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-mono">{formatPrice(stock.current_price)}</span>
                        </td>
                        <td className="p-4 text-right">
                          <span className={cn("font-mono", getChangeColor(stock.price_change_pct_24h))}>
                            {formatPercent(stock.price_change_pct_24h)}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-mono text-muted-foreground">
                            {formatNumber(stock.market_cap)}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          {stock.prediction ? (
                            <span className={cn("font-mono", getChangeColor(stock.prediction.predicted_change_pct))}>
                              {formatPercent(stock.prediction.predicted_change_pct)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          {stock.prediction ? (
                            <Badge variant="outline" className={cn(
                              stock.prediction.confidence >= 70 && "border-green-500 text-green-500",
                              stock.prediction.confidence >= 50 && stock.prediction.confidence < 70 && "border-yellow-500 text-yellow-500",
                              stock.prediction.confidence < 50 && "border-muted-foreground"
                            )}>
                              {stock.prediction.confidence}%
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            data-testid={`view-stock-${stock.ticker}`}
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/stocks/${stock.ticker}`);
                            }}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="lg:hidden">
              {/* Sort options for mobile */}
              <div className="p-3 border-b border-border/50 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Sort by:</span>
                <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setSortOrder('desc'); }}>
                  <SelectTrigger className="w-36 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ticker">Symbol</SelectItem>
                    <SelectItem value="current_price">Price</SelectItem>
                    <SelectItem value="predicted_change">AI Prediction</SelectItem>
                    <SelectItem value="confidence">Confidence</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {loading ? (
                <div className="p-4 space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Card key={i} className="bg-muted/30">
                      <CardContent className="p-4">
                        <Skeleton className="h-6 w-20 mb-2" />
                        <Skeleton className="h-4 w-32 mb-3" />
                        <div className="flex gap-4">
                          <Skeleton className="h-8 w-20" />
                          <Skeleton className="h-8 w-20" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : stocks.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No stocks found. Try adjusting your search or filters.
                </div>
              ) : (
                <div className="divide-y divide-border/50">
                  {stocks.map((stock) => (
                    <div
                      key={stock.stock_id}
                      className="p-4 hover:bg-accent/30 transition-colors cursor-pointer active:bg-accent/50"
                      onClick={() => navigate(`/stocks/${stock.ticker}`)}
                      data-testid={`stock-card-${stock.ticker}`}
                    >
                      {/* Top Row: Ticker + Price */}
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-lg">{stock.ticker}</span>
                            {stock.sector && (
                              <Badge variant="outline" className="text-xs font-normal hidden sm:inline-flex">
                                {stock.sector}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate max-w-[200px] sm:max-w-none">
                            {stock.name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold">{formatPrice(stock.current_price)}</p>
                          <p className={cn("font-mono text-sm", getChangeColor(stock.price_change_pct_24h))}>
                            {stock.price_change_pct_24h >= 0 ? <TrendingUp className="inline w-3 h-3 mr-1" /> : <TrendingDown className="inline w-3 h-3 mr-1" />}
                            {formatPercent(stock.price_change_pct_24h)}
                          </p>
                        </div>
                      </div>

                      {/* Bottom Row: AI Prediction */}
                      <div className="flex items-center justify-between pt-2 border-t border-border/30">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="text-xs text-muted-foreground">AI Prediction</p>
                            {stock.prediction ? (
                              <p className={cn("font-mono font-semibold", getChangeColor(stock.prediction.predicted_change_pct))}>
                                {formatPercent(stock.prediction.predicted_change_pct)}
                              </p>
                            ) : (
                              <p className="text-muted-foreground text-sm">Pending</p>
                            )}
                          </div>
                          {stock.prediction && (
                            <div>
                              <p className="text-xs text-muted-foreground">Confidence</p>
                              <Badge variant="outline" className={cn(
                                "font-mono",
                                stock.prediction.confidence >= 70 && "border-green-500 text-green-500",
                                stock.prediction.confidence >= 50 && stock.prediction.confidence < 70 && "border-yellow-500 text-yellow-500",
                                stock.prediction.confidence < 50 && "border-muted-foreground"
                              )}>
                                {stock.prediction.confidence}%
                              </Badge>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/stocks/${stock.ticker}`);
                          }}
                        >
                          Details
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t border-border/50">
                <span className="text-sm text-muted-foreground order-2 sm:order-1">
                  {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
                </span>
                <div className="flex gap-2 order-1 sm:order-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">Prev</span>
                  </Button>
                  <span className="flex items-center px-3 text-sm font-mono">
                    {page}/{totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
